import { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useAuth } from '../contexts/AuthContext'
import {
  clearErrors,
  clearUserData,
  fetchCurrentUser,
  fetchOrderByNumber,
  fetchUserOrders,
  fetchUserProfile,
  selectCurrentOrder,
  selectIsOrdersDataStale,
  selectIsProfileDataStale,
  selectIsUserDataStale,
  selectOrderError,
  selectOrderLoading,
  selectOrdersError,
  selectOrdersLoading,
  selectUser,
  selectUserError,
  selectUserIsAuthenticated,
  selectUserLoading,
  selectUserOrders,
  selectUserProfile,
  selectUserProfileError,
  selectUserProfileLoading,
  selectUserUpdateError,
  selectUserUpdating,
  setUser,
  setUserProfile,
  updateUserProfile,
} from '../store/slices/userSlice'
import { AppDispatch } from '../store/store'

export const useUserRedux = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { isAuthenticated } = useAuth()

  // Selectors
  const user = useSelector(selectUser)
  const userProfile = useSelector(selectUserProfile)
  const orders = useSelector(selectUserOrders)
  const currentOrder = useSelector(selectCurrentOrder)
  const isLoading = useSelector(selectUserLoading)
  const isProfileLoading = useSelector(selectUserProfileLoading)
  const isUpdating = useSelector(selectUserUpdating)
  const isOrdersLoading = useSelector(selectOrdersLoading)
  const isOrderLoading = useSelector(selectOrderLoading)
  const error = useSelector(selectUserError)
  const profileError = useSelector(selectUserProfileError)
  const updateError = useSelector(selectUserUpdateError)
  const ordersError = useSelector(selectOrdersError)
  const orderError = useSelector(selectOrderError)
  const isUserDataStale = useSelector(selectIsUserDataStale)
  const isProfileDataStale = useSelector(selectIsProfileDataStale)
  const isOrdersDataStale = useSelector(selectIsOrdersDataStale)
  const isUserAuthenticated = useSelector(selectUserIsAuthenticated)

  // Note: Selector values are used by components

  // Clear user data when authentication state changes to false
  useEffect(() => {
    if (!isAuthenticated && user) {
      dispatch(clearUserData())
    }
  }, [isAuthenticated, user, isLoading, dispatch])

  // Note: Auto-fetching user profile removed to prevent infinite loops

  // Actions
  const fetchUser = useCallback(() => {
    dispatch(fetchCurrentUser())
  }, [dispatch])

  const fetchProfile = useCallback(() => {
    dispatch(fetchUserProfile())
  }, [dispatch])

  const updateProfile = useCallback(
    (profileData: Parameters<typeof updateUserProfile>[0]) => {
      return dispatch(updateUserProfile(profileData))
    },
    [dispatch],
  )

  const fetchOrders = useCallback(
    (params?: Parameters<typeof fetchUserOrders>[0]) => {
      dispatch(fetchUserOrders(params || {}))
    },
    [dispatch],
  )

  const fetchOrder = useCallback(
    (orderNumber: string) => {
      dispatch(fetchOrderByNumber(orderNumber))
    },
    [dispatch],
  )

  const clearUser = useCallback(() => {
    dispatch(clearUserData())
  }, [dispatch])

  const clearAllErrors = useCallback(() => {
    dispatch(clearErrors())
  }, [dispatch])

  const setUserData = useCallback(
    (userData: Parameters<typeof setUser>[0]) => {
      dispatch(setUser(userData))
    },
    [dispatch],
  )

  const setProfileData = useCallback(
    (profileData: Parameters<typeof setUserProfile>[0]) => {
      dispatch(setUserProfile(profileData))
    },
    [dispatch],
  )

  // Refresh data if stale
  const refreshIfStale = useCallback(() => {
    if (isUserDataStale && isAuthenticated) {
      dispatch(fetchCurrentUser())
    }
    if (isProfileDataStale && user) {
      dispatch(fetchUserProfile())
    }
  }, [isUserDataStale, isProfileDataStale, isAuthenticated, user, dispatch])

  return {
    // Data
    user,
    userProfile,
    orders,
    currentOrder,

    // Loading states
    isLoading,
    isProfileLoading,
    isUpdating,
    isOrdersLoading,
    isOrderLoading,

    // Error states
    error,
    profileError,
    updateError,
    ordersError,
    orderError,

    // Computed states
    isAuthenticated: isUserAuthenticated,
    isUserDataStale,
    isProfileDataStale,
    isOrdersDataStale,

    // Actions
    fetchUser,
    fetchProfile,
    updateProfile,
    fetchOrders,
    fetchOrder,
    clearUser,
    clearAllErrors,
    setUserData,
    setProfileData,
    refreshIfStale,
  }
}

export default useUserRedux
